import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useOrders,
  useUpdateOrderStatus,
  useMarkOrderPickedUp,
  type Order,
} from "../queries/ordersQueries";
import { useMenuItems } from "../queries/menuQueries";
import { formatPrice } from "../utils/currency";
import {
  Container,
  Title,
  SimpleGrid,
  Card,
  Text,
  Select,
  Stack,
  Group,
  Badge,
  Center,
  Loader,
  Alert,
  Paper,
  Button,
  Switch,
  Anchor,
} from "@mantine/core";
import { IconAlertCircle, IconPhone, IconMessage2 } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";

const statusOptions: Array<{ value: Order["status"]; label: string }> = [
  { value: "confirmed", label: "Ny Ordre" },
  // { value: "pending", label: "Afventer" },
  { value: "preparing", label: "Tilbereder" },
  { value: "ready", label: "Klar" },
  { value: "cancelled", label: "Annulleret" },
];

const formatPhoneNumber = (value?: string | null) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 8) {
    return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  }
  if (digits.length === 10) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(
      4,
      6
    )} ${digits.slice(6)}`;
  }
  return value;
};

export default function RestaurantDashboard() {
  const { t } = useTranslation();

  //   TODO: Add status filter for orders
  const [statusFilter] = useState<string>("");
  const [soundEnabled, setSoundEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem("orderSoundEnabled") === "true";
    } catch {
      return false;
    }
  });
  const notificationSoundRef = useRef<HTMLAudioElement | null>(null);
  const lastOrderIdsRef = useRef<string[]>([]);

  const {
    data: orders,
    isLoading,
    error,
    refetch,
  } = useOrders(statusFilter || undefined);

  // Play sound when a new order appears
  useEffect(() => {
    if (!orders) {
      lastOrderIdsRef.current = [];
      return;
    }
    const currentIds = orders.map((o) => o.id);
    const prevIds = lastOrderIdsRef.current;
    const hasNewOrder =
      prevIds.length > 0 && currentIds.some((id) => !prevIds.includes(id));
    if (hasNewOrder && soundEnabled && notificationSoundRef.current) {
      notificationSoundRef.current.currentTime = 0;
      notificationSoundRef.current.play().catch(() => {
        // autoplay might be blocked until user interacts with the page
      });
    }
    lastOrderIdsRef.current = currentIds;
  }, [orders, soundEnabled]);

  useEffect(() => {
    const audio = new Audio("/new-order-compressed.mp3");
    audio.volume = 1;
    notificationSoundRef.current = audio;
  }, []);
  const { data: menuItems } = useMenuItems(false);
  const { mutate: updateOrderStatus } = useUpdateOrderStatus();
  const { mutate: markPickedUp } = useMarkOrderPickedUp();

  useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30_000);

    return () => clearInterval(interval);
  }, [refetch]);

  const [clearedAt, setClearedAt] = useState<string | null>(() => {
    try {
      return localStorage.getItem("ordersClearedAt");
    } catch {
      return null;
    }
  });

  const menuById = useMemo(() => {
    const map: Record<string, { name: string }> = {};
    (menuItems || []).forEach((m) => {
      map[m.id] = { name: m.name };
    });
    return map;
  }, [menuItems]);

  const visibleOrders = useMemo(() => {
    if (!orders) return [];
    if (!clearedAt) return orders;
    const clearedTime = new Date(clearedAt).getTime();
    return orders.filter((o) => new Date(o.created_on).getTime() > clearedTime);
  }, [orders, clearedAt]);

  const handleClearOrders = () => {
    const now = new Date().toISOString();
    try {
      localStorage.setItem("ordersClearedAt", now);
    } catch {
      // ignore storage errors
    }
    setClearedAt(now);
  };

  const handleStatusUpdate = async (
    orderId: string,
    newStatus: Order["status"]
  ) => {
    updateOrderStatus(
      {
        id: orderId,
        data: { status: newStatus },
      },
      {
        onSuccess: () => {
          notifications.show({
            title: t("admin.updateSuccess"),
            message: t("admin.updateSuccessMessage"),
            color: "teal",
          });
        },
        onError: (err) => {
          console.error("Failed to update order status:", err);
          notifications.show({
            title: t("admin.updateError"),
            message: t("admin.updateStatusError"),
            color: "red",
          });
        },
      }
    );
  };

  const handleMarkPickedUp = (orderId: string) => {
    markPickedUp(orderId, {
      onSuccess: () => {
        updateOrderStatus(
          {
            id: orderId,
            data: { status: "completed" },
          },
          {
            onSuccess: () => {
              notifications.show({
                title: "Ordre afsluttet",
                message: "Ordren er markeret som afhentet og afsluttet",
                color: "teal",
              });
            },
          }
        );
      },
      onError: (err) => {
        console.error("Failed to mark order as picked up:", err);
        notifications.show({
          title: "Fejl",
          message: "Kunne ikke opdatere ordre",
          color: "red",
        });
      },
    });
  };

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          title={t("admin.error")}
        >
          {t("admin.errorDescription")}
        </Alert>
      </Container>
    );
  }

  const activeOrders = visibleOrders?.filter(
    (order) =>
      !["completed", "cancelled"].includes(order.status) &&
      !order.picked_up &&
      order.payment_status !== "failed"
  );
  const completedOrders = visibleOrders?.filter(
    (order) => order.status === "completed" || order.status === "cancelled"
  );

  return (
    <Container size="xl" py="md">
      <Group justify="space-between" align="center" mb="lg">
        <Title order={1}>Restaurant Dashboard</Title>
        <Group gap="md">
          <Switch
            label="Lyd ved ny ordre"
            checked={soundEnabled}
            onChange={(event) => {
              const enabled = event.currentTarget.checked;
              setSoundEnabled(enabled);
              try {
                localStorage.setItem("orderSoundEnabled", String(enabled));
              } catch {
                // ignore
              }

              // unlock audio on IOS Safari with a user gesture
              if (enabled && notificationSoundRef.current) {
                notificationSoundRef.current.currentTime = 0;
                notificationSoundRef.current.play().catch(() => {
                  console.log("Audio play blocked", error);
                });
              }
            }}
          />
          <Button variant="light" color="red" onClick={handleClearOrders}>
            Ryd ordrer
          </Button>
        </Group>
      </Group>

      {/* Stats Cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="lg" mb="xl">
        <Card withBorder p="md">
          <Text size="sm" c="dimmed" mb="xs">
            Aktive Ordrer
          </Text>
          <Text size="xl" fw={700}>
            {activeOrders?.length || 0}
          </Text>
        </Card>
        <Card withBorder p="md">
          <Text size="sm" c="dimmed" mb="xs">
            Afventer
          </Text>
          <Text size="xl" fw={700}>
            {visibleOrders?.filter((o) => o.status === "pending").length || 0}
          </Text>
        </Card>
        <Card withBorder p="md">
          <Text size="sm" c="dimmed" mb="xs">
            Tilbereder
          </Text>
          <Text size="xl" fw={700}>
            {visibleOrders?.filter((o) => o.status === "preparing").length || 0}
          </Text>
        </Card>
        <Card withBorder p="md">
          <Text size="sm" c="dimmed" mb="xs">
            Klar
          </Text>
          <Text size="xl" fw={700}>
            {visibleOrders?.filter((o) => o.status === "ready").length || 0}
          </Text>
        </Card>
      </SimpleGrid>

      {/* Active Orders */}
      <Title order={2} mb="md">
        Aktive Ordrer
      </Title>
      {!activeOrders || activeOrders.length === 0 ? (
        <Paper withBorder p="xl" mb="xl">
          <Text ta="center" c="dimmed">
            Ingen aktive ordrer
          </Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg" mb="xl">
          {activeOrders.map((order) => (
            <Card key={order.id} withBorder p="md" data-testid="order-card">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text fw={600}>Ordre #{order.id.slice(0, 8)}</Text>
                  <Text size="sm" c="dimmed">
                    {new Date(order.created_on).toLocaleTimeString("da-DK", {
                      hour12: false,
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Text>
                </Group>

                {order.pickup_code && (
                  <Paper withBorder p="sm" style={{ textAlign: "center" }}>
                    <Text size="xs" c="dimmed" mb={4}>
                      Afhentningskode
                    </Text>
                    <Text
                      size="xl"
                      fw={700}
                      style={{
                        fontFamily: "monospace",
                        letterSpacing: "0.1em",
                        color: "var(--mantine-color-brand-6)",
                      }}
                    >
                      {order.pickup_code}
                    </Text>
                  </Paper>
                )}

                {(order.guest_phone || order.notes) && (
                  <Paper
                    withBorder
                    radius="md"
                    p="sm"
                    style={{
                      backgroundColor: "var(--mantine-color-gray-0)",
                    }}
                  >
                    <Stack gap="xs">
                      {order.guest_phone && (
                        <Group gap="xs" align="center">
                          <IconPhone
                            size={16}
                            color="var(--mantine-color-blue-6)"
                          />
                          <Badge color="blue" variant="light" size="sm">
                            Telefon
                          </Badge>
                          <Anchor
                            href={`tel:${order.guest_phone}`}
                            size="sm"
                            fw={700}
                            underline="hover"
                          >
                            {formatPhoneNumber(order.guest_phone)}
                          </Anchor>
                        </Group>
                      )}
                      {order.notes && (
                        <Group gap="xs" align="flex-start">
                          <IconMessage2
                            size={16}
                            color="var(--mantine-color-grape-6)"
                          />
                          <Badge color="grape" variant="light" size="sm">
                            Kommentar
                          </Badge>
                          <Text size="sm" c="dark.6">
                            {order.notes}
                          </Text>
                        </Group>
                      )}
                    </Stack>
                  </Paper>
                )}

                <Stack gap={4}>
                  {order.items.map((item) => (
                    <Text key={item.id} size="sm">
                      {item.quantity}×{" "}
                      {menuById[item.menu_item_id]?.name || "Item"} (#
                      {item.menu_item_id})
                    </Text>
                  ))}
                </Stack>

                <Text fw={700}>Total: {formatPrice(order.total_amount)}</Text>

                <Select
                  data-testid={`status-select-${order.id}`}
                  value={order.status}
                  onChange={(value) =>
                    value &&
                    handleStatusUpdate(order.id, value as Order["status"])
                  }
                  data={statusOptions}
                />

                {order.pickup_code && !order.picked_up && (
                  <Button
                    color="green"
                    fullWidth
                    onClick={() => handleMarkPickedUp(order.id)}
                  >
                    Marker som afhentet
                  </Button>
                )}
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* Completed Orders */}
      <Title order={2} mb="md">
        Afsluttede Ordrer
      </Title>
      {!completedOrders || completedOrders.length === 0 ? (
        <Paper withBorder p="xl">
          <Text ta="center" c="dimmed">
            Ingen afsluttede ordrer
          </Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {completedOrders.slice(0, 10).map((order) => (
            <Paper key={order.id} withBorder p="md">
              <Group justify="space-between" wrap="wrap" mb="xs">
                <Text fw={600}>Ordre #{order.id.slice(0, 8)}</Text>
                <Text size="sm" c="dimmed">
                  {new Date(order.created_on).toLocaleString("da-DK", {
                    hour12: false,
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Text>
                <Badge color={order.status === "completed" ? "gray" : "red"}>
                  {t(`orders.status.${order.status}`)}
                </Badge>
                <Text fw={600}>{formatPrice(order.total_amount)}</Text>
              </Group>
              <Stack gap={4}>
                {order.items.map((item) => (
                  <Text key={item.id} size="sm" c="dimmed">
                    {item.quantity}×{" "}
                    {menuById[item.menu_item_id]?.name || "Vare"}
                  </Text>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </Container>
  );
}
